import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Star, ChevronLeft, ChevronRight, Filter, Play, CheckCircle, ThumbsUp, ExternalLink, Video } from 'lucide-react';

const Testimonials = ({ tenant = 'choice' }) => {
  // ===== STATE MANAGEMENT =====
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterRating, setFilterRating] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'carousel'
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // ===== FETCH TESTIMONIALS =====
  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await fetch(`/tenant-content/${tenant}/testimonials.json`);
      const data = await response.json();
      setTestimonials(data.testimonials || []);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  // ===== FILTER TESTIMONIALS (useMemo to fix use-before-define) =====
  const filteredTestimonials = useMemo(() => {
    return testimonials.filter(t => {
      const matchesRating = filterRating === 'all' || t.rating === parseInt(filterRating);
      const matchesType = filterType === 'all' || t.type === filterType;
      return matchesRating && matchesType;
    });
  }, [testimonials, filterRating, filterType]);

  // ===== CALCULATE STATISTICS =====
  const stats = useMemo(() => ({
    total: testimonials.length,
    avgRating: testimonials.length > 0 
      ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1)
      : 0,
    fiveStars: testimonials.filter(t => t.rating === 5).length,
    creators: testimonials.filter(t => t.type === 'creator').length,
    investors: testimonials.filter(t => t.type === 'investor').length,
    withVideo: testimonials.filter(t => t.videoUrl).length
  }), [testimonials]);

  // ===== AUTO-PLAY CAROUSEL =====
  useEffect(() => {
    if (!isAutoPlay || viewMode !== 'carousel' || filteredTestimonials.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredTestimonials.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [isAutoPlay, viewMode, currentIndex, filteredTestimonials.length]);

  // ===== NAVIGATION FUNCTIONS =====
  const nextTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % filteredTestimonials.length);
  }, [filteredTestimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + filteredTestimonials.length) % filteredTestimonials.length);
  }, [filteredTestimonials.length]);

  // ===== RENDER STAR RATING =====
  const renderStars = useCallback((rating, size = 'w-5 h-5') => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`${size} ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  }, []);

  // ===== TESTIMONIAL CARD COMPONENT =====
  const TestimonialCard = useCallback(({ testimonial, isCarousel = false }) => {
    return (
      <div className={`${isCarousel ? '' : 'bg-white rounded-2xl p-6 lg:p-8 shadow-sm hover:shadow-lg transition-all border border-slate-200'}`}>
        {/* Video Thumbnail (if video testimonial) */}
        {testimonial.videoUrl && (
          <div className="relative mb-4 rounded-xl overflow-hidden group">
            <img 
              src={testimonial.videoThumbnail || testimonial.avatar} 
              alt="Video testimonial"
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-all flex items-center justify-center">
              <a 
                href={testimonial.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 rounded-full bg-white/90 hover:bg-white hover:scale-110 transition-all flex items-center justify-center"
              >
                <Play className="w-8 h-8 text-blue-600 ml-1" />
              </a>
            </div>
            <div className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1">
              <Video className="w-3 h-3" />
              Video
            </div>
          </div>
        )}

        {/* Rating */}
        <div className="mb-4">
          {renderStars(testimonial.rating)}
        </div>

        {/* Testimonial Text */}
        <p className={`text-slate-700 leading-relaxed mb-6 ${isCarousel ? 'text-lg' : 'text-base'}`}>
          "{testimonial.text}"
        </p>

        {/* Author Info */}
        <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
          <img 
            src={testimonial.avatar} 
            alt={testimonial.name} 
            className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-100" 
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-slate-900">{testimonial.name}</div>
              {testimonial.verified && (
                <CheckCircle className="w-4 h-4 text-blue-600" title="Verified" />
              )}
            </div>
            <div className="text-sm text-slate-600">{testimonial.role}</div>
            {testimonial.channel && (
              <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
                {testimonial.channel}
                {testimonial.channelUrl && (
                  <a href={testimonial.channelUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="text-right">
            {testimonial.subscribers && (
              <div className="text-sm font-semibold text-slate-900">{testimonial.subscribers}</div>
            )}
            {testimonial.monthlyEarnings && (
              <div className="text-xs text-green-600 font-medium">{testimonial.monthlyEarnings}/mo</div>
            )}
            {testimonial.investment && (
              <div className="text-sm font-semibold text-slate-900">{testimonial.investment}</div>
            )}
            {testimonial.monthlyReturns && (
              <div className="text-xs text-green-600 font-medium">{testimonial.monthlyReturns}/mo</div>
            )}
          </div>
        </div>

        {/* Helpful Badge */}
        {testimonial.helpful && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <ThumbsUp className="w-4 h-4" />
            <span>{testimonial.helpful} people found this helpful</span>
          </div>
        )}

        {/* Date */}
        {testimonial.date && (
          <div className="mt-2 text-xs text-slate-500">
            {new Date(testimonial.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        )}
      </div>
    );
  }, [renderStars]);

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-3 text-slate-600">Loading testimonials...</p>
        </div>
      </section>
    );
  }

  // ===== NO TESTIMONIALS STATE =====
  if (testimonials.length === 0) {
    return (
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-purple-100 mb-4">
              <CheckCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold mb-3">Our Creators &amp; Investors Are Onboarding</h2>
            <p className="text-lg text-slate-600 mb-2">
              We are in soft launch. The first cohort of hand-picked creators is going through onboarding.
            </p>
            <p className="text-base text-slate-500">
              Their voices &mdash; the videos uploaded, the revenue earned, the films acquired &mdash; will appear here as they roll in.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Header with Statistics */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-3">What Our Community Says</h2>
          <p className="text-lg text-slate-600 mb-6">Real experiences from creators and investors in our network</p>
          
          {/* Statistics Cards */}
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <div className="bg-white rounded-xl px-6 py-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2">
                {renderStars(5, 'w-4 h-4')}
                <span className="text-2xl font-bold text-slate-900">{stats.avgRating}</span>
              </div>
              <p className="text-xs text-slate-600 mt-1">Average Rating</p>
            </div>
            <div className="bg-white rounded-xl px-6 py-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <p className="text-xs text-slate-600 mt-1">Total Reviews</p>
            </div>
            <div className="bg-white rounded-xl px-6 py-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl font-bold text-green-600">{stats.fiveStars}</div>
              <p className="text-xs text-slate-600 mt-1">5-Star Reviews</p>
            </div>
            <div className="bg-white rounded-xl px-6 py-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl font-bold text-red-600">{stats.withVideo}</div>
              <p className="text-xs text-slate-600 mt-1">Video Reviews</p>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setViewMode('carousel')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  viewMode === 'carousel'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Carousel
              </button>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                showFilters
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>

            {/* Auto-play Toggle (Carousel only) */}
            {viewMode === 'carousel' && (
              <button
                onClick={() => setIsAutoPlay(!isAutoPlay)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isAutoPlay
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Play className="w-4 h-4" />
                Auto-play {isAutoPlay ? 'On' : 'Off'}
              </button>
            )}

            {/* Results Counter */}
            <div className="ml-auto text-sm text-slate-600">
              Showing <span className="font-semibold">{filteredTestimonials.length}</span> of {testimonials.length} reviews
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-white rounded-xl shadow-sm p-4 mt-2 animate-slideDown">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Filter by Rating
                  </label>
                  <select
                    value={filterRating}
                    onChange={(e) => setFilterRating(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Ratings</option>
                    <option value="5">5 Stars ⭐⭐⭐⭐⭐</option>
                    <option value="4">4 Stars ⭐⭐⭐⭐</option>
                    <option value="3">3 Stars ⭐⭐⭐</option>
                  </select>
                </div>

                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Filter by Type
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Types</option>
                    <option value="creator">🎬 Creators ({stats.creators})</option>
                    <option value="investor">💰 Investors ({stats.investors})</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              {(filterRating !== 'all' || filterType !== 'all') && (
                <button
                  onClick={() => {
                    setFilterRating('all');
                    setFilterType('all');
                  }}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Testimonials Display */}
        {filteredTestimonials.length === 0 ? (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No testimonials match your filters.</p>
            <button
              onClick={() => {
                setFilterRating('all');
                setFilterType('all');
              }}
              className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredTestimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        ) : (
          /* Carousel View */
          <div className="max-w-4xl mx-auto relative">
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
              <TestimonialCard 
                testimonial={filteredTestimonials[currentIndex]} 
                isCarousel={true}
              />
            </div>

            {/* Carousel Navigation */}
            {filteredTestimonials.length > 1 && (
              <>
                <button
                  onClick={prevTestimonial}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center text-slate-700"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center text-slate-700"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Carousel Indicators */}
                <div className="flex justify-center gap-2 mt-6">
                  {filteredTestimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentIndex ? 'bg-blue-600 w-8' : 'bg-slate-300 w-2 hover:bg-slate-400'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Counter */}
                <div className="text-center mt-3 text-sm text-slate-600">
                  {currentIndex + 1} / {filteredTestimonials.length}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;